/**
 * Union tag aggregation, and the queries rules ask of the result.
 *
 * Aggregation unions the selected particles' tags into a per-axis multiset and
 * stops there. Nothing is collapsed, averaged, or reduced to a single value —
 * reduction happens inside a predicate, at query time (design D2).
 *
 * That distinction is the whole expressive difference between this and a score
 * vector. A `max`-collapsed `risk` cannot answer "is there *any* medium risk in
 * here?", and a plain set cannot answer "is the worst risk still below high?".
 * Keeping the multiset lets an author ask both of the same assembly.
 */
import { AXES, ORDINAL_AXES, ordinalIndex } from '@managing-up/schema';
/** An aggregate with every axis present and empty. */
export function emptyAggregate() {
    const aggregate = {};
    for (const axis of AXES)
        aggregate[axis] = [];
    return aggregate;
}
/** True for axes that declare an order, and so accept `max` and `min`. */
export function isOrdinal(axis) {
    return ORDINAL_AXES.includes(axis);
}
/** A `TagSet`'s axis/value pairs, normalising the scalar-or-array shorthand. */
export function tagSetEntries(tags) {
    if (tags === undefined)
        return [];
    const entries = [];
    for (const axis of AXES) {
        const raw = tags[axis];
        if (raw === undefined)
            continue;
        for (const value of Array.isArray(raw) ? raw : [raw])
            entries.push([axis, value]);
    }
    return entries;
}
/**
 * Unions any number of `TagSet`s into one multiset.
 *
 * Values arrive in argument order, and within an argument in vocabulary axis
 * order, so the same selection always produces the same aggregate — the trace
 * is compared byte-for-byte in tests.
 */
export function aggregateTagSets(sets) {
    const aggregate = emptyAggregate();
    for (const set of sets) {
        for (const [axis, value] of tagSetEntries(set))
            aggregate[axis].push(value);
    }
    return aggregate;
}
/** The values recorded on one axis, with multiplicity, in insertion order. */
export function valuesOn(aggregate, axis) {
    return aggregate[axis];
}
/** How many times `value` appears on `axis`. */
export function countOf(aggregate, axis, value) {
    let count = 0;
    for (const entry of aggregate[axis])
        if (entry === value)
            count += 1;
    return count;
}
/** How many *different* values appear on `axis`. */
export function distinctOn(aggregate, axis) {
    const seen = [];
    for (const entry of aggregate[axis])
        if (!seen.includes(entry))
            seen.push(entry);
    return seen.length;
}
/**
 * The highest position present on an ordinal axis, or `-1` when the axis is
 * empty. Values the vocabulary does not declare are ignored; the pack cannot
 * reach the engine carrying one, because `createGame` runs the vocabulary pass.
 */
export function maxOrdinal(aggregate, axis) {
    let best = -1;
    for (const entry of aggregate[axis]) {
        const position = ordinalIndex(axis, entry);
        if (position > best)
            best = position;
    }
    return best;
}
/** The lowest position present on an ordinal axis, or `-1` when the axis is empty. */
export function minOrdinal(aggregate, axis) {
    let best = -1;
    for (const entry of aggregate[axis]) {
        const position = ordinalIndex(axis, entry);
        if (position < 0)
            continue;
        if (best < 0 || position < best)
            best = position;
    }
    return best;
}
// ---------------------------------------------------------------------------
// Mutation — how a node alters the travelling proposal before forwarding it
// ---------------------------------------------------------------------------
/** Adds one occurrence of `value` on `axis`. */
export function addTag(aggregate, axis, value) {
    return { ...aggregate, [axis]: [...aggregate[axis], value] };
}
/** Removes every occurrence of `value` from `axis`. */
export function removeTag(aggregate, axis, value) {
    return { ...aggregate, [axis]: aggregate[axis].filter((entry) => entry !== value) };
}
/** Empties an axis entirely. */
export function clearAxis(aggregate, axis) {
    return { ...aggregate, [axis]: [] };
}
/**
 * A canonical rendering of an aggregate: axes in vocabulary order, values
 * sorted within each axis. Used by the semantic state hash, where two
 * aggregates holding the same multiset must hash alike whatever order the
 * values arrived in.
 */
export function canonicalAggregate(aggregate) {
    const parts = [];
    for (const axis of AXES) {
        const values = aggregate[axis];
        if (values.length === 0)
            continue;
        parts.push(`${axis}:${[...values].sort().join(',')}`);
    }
    return parts.join('|');
}
//# sourceMappingURL=tags.js.map